import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Message } from '@adornis/mails/server/communication.js';
import type { Bundesland } from '@chancenwerk/enums/enums.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { Adresse } from '../../../adresse.js';
import { MailEventLog, MailEventLogType } from '../../../eventlogs/MailEventlog.js';
import { registerExternalTeacher } from '../../../mailer/mailTemplates.js';
import { ExternalKPFRelationship } from '../../../semantics/ExternalKPFRelationship.js';
import { PartnerRelationship } from '../../../semantics/PartnerRelationship.js';
import { Partner } from '../../partner/entities/Partner.js';
import { ChancenwerkUser } from './ChancenwerkUser.js';

/**
 * @entity BusinessAddressPartnerInformation
 */
@Entity()
export class BusinessAddressPartnerInformation extends AdornisEntity {
  public static override _class = 'BusinessAddressPartnerInformation';

  @Field(() => Adresse)
  public address: Maybe<Adresse>;

  @Field(() => String)
  public partnerID: Maybe<string>;

  @Field(() => String)
  public partnerName: Maybe<string>;
}

/**
 * @entity ExternalContactPerson
 */
@Entity()
export class ExternalContactPerson extends ChancenwerkUser {
  public static override _class = 'ExternalContactPerson';
  public static override readableName = 'externe Kontaktperson';

  @Field(() => String)
  public telefon: Maybe<string>;

  @Field(() => Boolean, { default: v => v ?? false })
  public activated?: boolean;

  @Field(() => Boolean, { default: v => v ?? false })
  public registeredViaToken: Maybe<boolean>;

  /**
   * This field tells whether the user uses the businessaddress of the connected partner or the address is custom
   */
  @Field(() => Boolean, { default: v => v ?? false })
  public customBusinessAddressEnabled: Maybe<boolean>;

  /**
   * custom address if the user is not connected
   */
  @Field(() => Adresse, { default: v => v ?? new Adresse({}) })
  public customBusinessAddress: Maybe<Adresse>;

  @Field(() => String)
  public customBusinessBundesland: Maybe<Bundesland>;

  @Field(() => String)
  public businessAddressPartnerID: Maybe<string>;

  @Field(() => String)
  public businessEmail: Maybe<string>;

  @Field(() => String)
  public businessTelefone: Maybe<string>;

  @Field(() => String)
  public businessSecondTelefone: Maybe<string>;

  @Field(() => String)
  public businessAddressExtra: Maybe<string>;

  /**
   * Resolves to the correct businessaddress of the user. If its not custom it resolves to the current address of the partner
   */
  @Field(() => BusinessAddressPartnerInformation, {
    resolve: function (this: ExternalContactPerson) {
      return async () => {
        if (this.businessAddress) return this.businessAddress;
        if (!this.businessAddressPartnerID) {
          const res = (await loadMongoData(ExternalContactPerson, this._id, {
            projection: {
              customBusinessAddress: 1,
            },
          })) as Maybe<ExternalContactPerson>;
          return new BusinessAddressPartnerInformation({
            address: res?.customBusinessAddress,
          });
        }

        const partnerCollection = await getRawCollection<Partner>(Partner._collectionName);
        const businessAddressPartner = (await partnerCollection.findOne({
          _id: this.businessAddressPartnerID,
        })) as Maybe<Partner>;
        if (!businessAddressPartner) {
          logger.error(
            'Cannot resolve the address of the partner with _id: ' +
              this.businessAddressPartnerID +
              '. Is it an orphan?',
          );
          return null;
        }
        return new BusinessAddressPartnerInformation({
          address: businessAddressPartner.address,
          partnerID: businessAddressPartner._id,
          partnerName: (constructValue(businessAddressPartner) as Partner).toString(),
        });
      };
    },
  })
  public businessAddress: Maybe<BusinessAddressPartnerInformation>;

  @Field(() => [PartnerRelationship], {
    resolve: function (this: ExternalContactPerson) {
      return async () => {
        const relationshipType = await import('../../../semantics/PartnerRelationship.js');
        return (await loadMongoData(relationshipType.PartnerRelationship, {
          _class: relationshipType.PartnerRelationship._class,
          subjectID: this._id,
        })) as Maybe<PartnerRelationship[]>;
      };
    },
  })
  public partnerRelations: Maybe<PartnerRelationship[]>;

  @Field(() => [ExternalKPFRelationship], {
    resolve: function (this: ExternalContactPerson) {
      return async () => {
        const relationshipType = await import('../../../semantics/ExternalKPFRelationship.js');
        return (await loadMongoData(relationshipType.ExternalKPFRelationship, {
          _class: relationshipType.ExternalKPFRelationship._class,
          subjectID: this._id,
        })) as Maybe<ExternalKPFRelationship[]>;
      };
    },
  })
  public kpfRelations: Maybe<ExternalKPFRelationship[]>;

  @Query(() => Boolean)
  public static externaleIsActivated(@Arg('email', () => String) email: string) {
    return async (gqlFields: void) => {
      const userCol = await getRawCollection<ExternalContactPerson>(this._collectionName);
      const user = constructValue(
        await userCol.findOne({ _class: this._class, $or: [{ email }, { username: email }] }),
      ) as ExternalContactPerson;
      if (user) {
        if (user.activated) return true;
        else return false;
      }
      return true;
    };
  }

  @Subscription(() => [ExternalContactPerson])
  public static subscribeAllAccountRequests(@Arg('kpfID', () => String) kpfID: string) {
    return (gqlFields: BaseQLSelectionSet<ExternalContactPerson>) => {
      const selector = {
        _class: this._class,
        activated: false,
        registeredViaToken: true,
        'roles.contexts': `kpf_${kpfID}`,
      };
      const handle = getCollectionHandle<ExternalContactPerson>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as ExternalContactPerson[]);
        }),
      );
    };
  }

  protected override async saveMailEventLog(error?: Error) {
    await new MailEventLog({
      userID: this._id,
      mailType: MailEventLogType.CONTACT_PERSON_REGISTER,
      successful: !error,
      sendMailError: error?.message,
      emailAddress: this.email,
    }).create();
  }

  public override async getComposedRegisterMail() {
    return Message.compose({
      subject: 'Willkommen auf dem CHANCENcampus',
      html: registerExternalTeacher({
        username: this.username,
        confirmationUrl: (await ExternalContactPerson.getPasswordChangeLink(this._id, 'chancencampus')).toString(),
      }),
    });
  }
}
